// @ts-nocheck
import {
  IconCurrencyDollar,
  IconShoppingCart,
  IconUsers,
  IconPercentage,
  IconTrendingUp,
  IconTrendingDown,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Box, Button, Card, Group, Progress, SimpleGrid, Stack, Table, Tabs, Text, Title } from '@/components/ui';
import { ecommerceStats, mockOrders, mockProducts, revenueByMonth, ordersByStatus } from '../mockData/ecommerce';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color
}: {
  title: string;
  value: string;
  change: number;
  icon: React.ElementType;
  color: string;
}) => {
  const isPositive = change >= 0;
  return (
    <Card>
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed">{title}</Text>
        <Box
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            backgroundColor: `var(--mantine-color-${color}-light)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
        </Box>
      </Group>
      <Text size="xl" fw={700}>{value}</Text>
      <Group gap="xs" mt="xs">
        {isPositive ? (
          <IconTrendingUp size={16} color="var(--mantine-color-green-filled)" />
        ) : (
          <IconTrendingDown size={16} color="var(--mantine-color-red-filled)" />
        )}
        <Text size="xs" c={isPositive ? 'green' : 'red'}>
          {isPositive ? '+' : ''}{change}%
        </Text>
        <Text size="xs" c="dimmed">vs last month</Text>
      </Group>
    </Card>
  );
};

export default function EcommerceDashboardPage() {
  const navigate = useNavigate();
  const recentOrders = mockOrders.slice(0, 5);
  const topProducts = mockProducts.slice(0, 5);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>E-Commerce Dashboard</Title>
          <Text c="dimmed" size="sm">Monitor your store performance and sales</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('products')}>View Products</Button>
          <Button onClick={() => navigate('orders')}>View Orders</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Revenue"
          value={formatCurrency(ecommerceStats.totalRevenue)}
          change={ecommerceStats.revenueChange}
          icon={IconCurrencyDollar}
          color="green"
        />
        <StatCard
          title="Total Orders"
          value={ecommerceStats.totalOrders.toString()}
          change={ecommerceStats.ordersChange}
          icon={IconShoppingCart}
          color="blue"
        />
        <StatCard
          title="Total Customers"
          value={ecommerceStats.totalCustomers.toString()}
          change={ecommerceStats.customersChange}
          icon={IconUsers}
          color="violet"
        />
        <StatCard
          title="Conversion Rate"
          value={`${ecommerceStats.conversionRate}%`}
          change={ecommerceStats.conversionChange}
          icon={IconPercentage}
          color="orange"
        />
      </SimpleGrid>

      {/* Charts Row */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Revenue Chart Card */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Revenue Overview</Text>
            <Tabs
              variant="pills"
              defaultValue="monthly"
              data={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' },
              ]}
            />
          </Group>
          {/* Simple bar representation */}
          <Stack gap="xs">
            {revenueByMonth.slice(-6).map((item) => (
              <Group key={item.month} gap="xs">
                <Text size="xs" w={30}>{item.month}</Text>
                <Progress
                  value={(item.revenue / 50000) * 100}
                  size="lg"
                  radius="xl"
                  style={{ flex: 1 }}
                />
                <Text size="xs" w={70} ta="right">{formatCurrency(item.revenue)}</Text>
              </Group>
            ))}
          </Stack>
        </Card>

        {/* Orders by Status */}
        <Card>
          <Text fw={600} mb="md">Orders by Status</Text>
          <Stack gap="md">
            {ordersByStatus.map((item) => (
              <Group key={item.status} justify="space-between">
                <Group gap="sm">
                  <Badge color={item.color} variant="filled" size="sm" circle>
                    {item.count}
                  </Badge>
                  <Text size="sm">{item.status}</Text>
                </Group>
                <Text size="sm" fw={500}>{item.count} orders</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Tables Row */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Recent Orders */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recent Orders</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('orders')}
            >
              View All
            </Button>
          </Group>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recentOrders.map((order) => (
                <Table.Tr key={order.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{order.orderNumber}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{order.customerName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      color={
                        order.status === 'delivered' ? 'green' :
                        order.status === 'shipped' ? 'cyan' :
                        order.status === 'processing' ? 'blue' :
                        order.status === 'pending' ? 'yellow' :
                        order.status === 'cancelled' ? 'red' : 'gray'
                      }
                    >
                      {order.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>{formatCurrency(order.total)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        {/* Top Products */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Top Products</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('products')}
            >
              View All
            </Button>
          </Group>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Stock</Table.Th>
                <Table.Th ta="right">Price</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {topProducts.map((product) => (
                <Table.Tr key={product.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar size="sm" radius="md" color="blue">
                        {product.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Text size="sm" fw={500} lineClamp={1}>{product.name}</Text>
                        <Text size="xs" c="dimmed">{product.category}</Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      color={product.stock > 50 ? 'green' : product.stock > 20 ? 'yellow' : 'red'}
                      variant="light"
                    >
                      {product.stock} in stock
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>{formatCurrency(product.price)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
